import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { WipConfig } from '../../types';
import { useAccordionTableReducer } from '../useAccordionTableReducer';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import {
  CreditAgreement,
  getBooleanSymbol,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditUserModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditUserModal';
import { PropertyTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/PropertyTable';

type UserAccordionRowProps = {
  user: CreditAgreement.DomainEntity.Items.UserEntity;
  wipConfig?: WipConfig;
  index: number;
  basePropertyKey: string;
  organizationId: string;
  setOpenedAccordionIndices: React.Dispatch<{
    isExpanded: boolean;
    index: number;
  }>;
  openedAccordionIndices: number[];
  columnWidth: number;
};
export const UserAccordionRow = ({
  user,
  wipConfig,
  index,
  basePropertyKey,
  organizationId,
  setOpenedAccordionIndices,
  openedAccordionIndices,
  columnWidth,
}: UserAccordionRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const basePropertyKeyWithIdx = `${basePropertyKey}[${index}]`;
  const { entity, section, _isWrittenIn_ } = user;
  const { firstName, lastName, email } = entity;

  const columnWidthStr = `${columnWidth}%`;

  const sanitizedUserProperties = _.omitBy(
    _.omit(user, 'entity', '_isWrittenIn_', 'section'),
    _.isNil,
  );
  const userPropertyPairs = _.toPairs(sanitizedUserProperties);
  const DropdownTd = () => {
    if (!userPropertyPairs.length) {
      return <Td width="5%" />;
    }
    if (_.includes(openedAccordionIndices, index)) {
      return (
        <Td width="5%">
          <ChevronUpIcon />
        </Td>
      );
    }
    return (
      <Td width="5%">
        <ChevronDownIcon />
      </Td>
    );
  };

  return (
    <AccordionItem>
      {({ isExpanded }) => (
        <>
          <AccordionButton
            onClick={() =>
              setOpenedAccordionIndices({
                isExpanded,
                index,
              })
            }
            style={{ padding: 0 }}
          >
            <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
              <Tbody>
                <Tr>
                  <Td width={columnWidthStr}>{firstName}</Td>
                  <Td width={columnWidthStr}>{lastName}</Td>
                  <Td width={columnWidthStr}>{email}</Td>
                  <Td width={columnWidthStr}>{section || '-'}</Td>
                  {_isWrittenIn_ && (
                    <Td width={columnWidthStr}>
                      {getBooleanSymbol(_isWrittenIn_)}
                    </Td>
                  )}
                  {wipConfig?.isMutateAllowed(basePropertyKey) && (
                    <Td width={columnWidthStr}>
                      <EditIconButton
                        ariaLabel="edit"
                        label="Edit"
                        onClick={(event) => {
                          setIsOpen(true);
                          if (event) {
                            event.stopPropagation();
                          }
                        }}
                      />
                      <DeleteArrayPropertyButton
                        wipConfig={wipConfig}
                        basePropertyKey={basePropertyKey}
                        idx={index}
                      />
                      {!getIn(wipConfig.wipShell, basePropertyKeyWithIdx) && (
                        <EditUserModal
                          isOpen={isOpen}
                          setIsOpen={setIsOpen}
                          organizationId={organizationId}
                          basePropertyKey={basePropertyKeyWithIdx}
                          wipConfig={wipConfig}
                        />
                      )}
                    </Td>
                  )}
                  <DropdownTd />
                </Tr>
              </Tbody>
            </Table>
          </AccordionButton>

          <AccordionPanel paddingRight="0">
            <Box height="20px" />
            <Text fontSize="16px" mb="10px" fontWeight="bold">
              Contact properties
            </Text>
            <PropertyTable
              propertyPairs={userPropertyPairs}
              basePropertyKey={basePropertyKeyWithIdx}
              wipConfig={wipConfig}
            />
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};

type UserEntityAccordionTableProps = {
  users: CreditAgreement.DomainEntity.Items.UserEntity[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  organizationId: string;
};
export const UserEntityAccordionTable = ({
  users,
  wipConfig,
  basePropertyKey,
  organizationId,
}: UserEntityAccordionTableProps) => {
  const {
    openedAccordionIndices,
    setOpenedAccordionIndices,
  } = useAccordionTableReducer();

  const userEntityAccordionTableHeaders = [
    'First Name',
    'Last Name',
    'Email',
    'Section',
  ];

  const showIsWrittenIn =
    _.filter(users, (user) => {
      return !_.isNil(user._isWrittenIn_);
    }).length > 0;
  if (showIsWrittenIn) {
    userEntityAccordionTableHeaders.push('Is Written In');
  }
  if (wipConfig) {
    userEntityAccordionTableHeaders.push('Action');
  }

  // Even column widths with fixed tableLayout so the headers and the accordion rows are always aligned. Dropdown column is fixed to 5%
  // The headers and rows have to be rendered in different tables because an accordion component cannot correctly wrap around an entire table row
  const columnWidth = 95 / userEntityAccordionTableHeaders.length;

  return (
    <>
      <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
        <Thead>
          <Tr>
            {_.map(userEntityAccordionTableHeaders, (header) => {
              return (
                <Th width={`${columnWidth}%`} key={`th-${header}`}>
                  {header}
                </Th>
              );
            })}
            <Th width="5%" />
          </Tr>
        </Thead>
      </Table>
      <Accordion allowMultiple index={openedAccordionIndices} pl="0">
        {_.map(users, (user, index) => {
          return (
            <UserAccordionRow
              key={`accordion-row-${user.entity.id}-${index}`}
              user={user}
              index={index}
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              organizationId={organizationId}
              setOpenedAccordionIndices={setOpenedAccordionIndices}
              openedAccordionIndices={openedAccordionIndices}
              columnWidth={columnWidth}
            />
          );
        })}
      </Accordion>
    </>
  );
};
